import { useCallback, useState } from "react";
import PaymentMethodModal from "../../../components/common/PaymentMethodModal";
import {
  ConfirmationMethodProps,
  ConfirmationMethodResult,
  SelectedPaymentType,
  SnackBarStates,
} from "../../../components/common/PaymentMethodModal/types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import { createMultiCashout } from "../../../setup/api/services/multiCashouts";
import { WithdrawalExecutorType } from "../../../setup/api/services/withdraws/models";
import { IApiResponse } from "../../../setup/api/models";
import { ICreateMultiCashoutResponseData } from "../../../setup/api/services/multiCashouts/models";

interface IMultiCashoutModal {
  open: boolean;
  onClose: () => void;
  paymentAmount: number;
  userId?: number | string;
  beneficiaryName: string;
}

const MultiCashoutModal = ({
  open,
  onClose,
  paymentAmount,
  userId,
  beneficiaryName,
}: IMultiCashoutModal) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [amount, setAmount] = useState<number | string>();
  const [gatewayPaymentId, setGatewayPaymentId] = useState<string>();
  const [targetContractDomainId, setTargetContractDomainId] = useState<
    string | undefined
  >();
  const [transactionDate, setTransactionDate] = useState<Date | null>(
    new Date()
  );

  const handleSingleViewExecuteTransfer = useCallback(
    async (id: string): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      let shouldClose = false;
      let success = false;

      let result: IApiResponse<ICreateMultiCashoutResponseData> | undefined;
      let isSuccess = false;
      try {
        result = await createMultiCashout({
          beneficiaryId: Number(id),
          amount: paymentAmount?.toString(),
          beneficiaryName,
        });
        isSuccess = !!result?.successful;
        if (isSuccess) {
          snackBarStates = {
            title: t("lbl_cash_out_completed"),
            color: "success",
          };
          shouldClose = true;
          success = true;
        } else {
          snackBarStates = { title: t("lbl_cash_out_failed"), color: "error" };
          success = false;
        }
      } catch (error: any) {
        snackBarStates = {
          title: JSON.stringify(error?.message),
          color: "error",
        };
        success = false;
      } finally {
        shouldClose = true;
        if (isSuccess && result?.data?.withdrawalId) {
          setTimeout(
            () => navigate(`${ROUTES.WITHDRAWS}/${result?.data?.withdrawalId}`),
            3000
          );
        } else {
          setTimeout(() => window.location.reload(), 3000);
        }
      }
      return { snackBarStates, shouldClose, success };
    },
    [beneficiaryName, paymentAmount, t, navigate]
  );

  const handleManualTransfer = useCallback(
    async (id: string): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      let shouldClose = false;
      let success = false;

      let result: IApiResponse<ICreateMultiCashoutResponseData> | undefined;
      let isSuccess = false;
      try {
        result = await createMultiCashout({
          beneficiaryId: Number(id),
          amount: paymentAmount?.toString(),
          beneficiaryName,
          withdrawalExecute: {
            withdrawalExecutorType: WithdrawalExecutorType.MANUAL,
            gatewayPaymentId: String(gatewayPaymentId),
            transactionDate: transactionDate
              ? transactionDate?.toISOString()
              : new Date().toISOString(),
          },
        });
        isSuccess = !!result?.successful;
        if (isSuccess) {
          snackBarStates = {
            title: t("lbl_cash_out_completed"),
            color: "success",
          };
        }
      } catch (error: any) {
        snackBarStates = {
          title: JSON.stringify(error?.message),
          color: "error",
        };
        success = false;
      } finally {
        shouldClose = true;
        if (isSuccess && result?.data?.withdrawalId) {
          setTimeout(
            () => navigate(`${ROUTES.WITHDRAWS}/${result?.data?.withdrawalId}`),
            3000
          );
        } else {
          setTimeout(() => window.location.reload(), 3000);
        }
      }
      return { snackBarStates, shouldClose, success };
    },
    [
      beneficiaryName,
      gatewayPaymentId,
      navigate,
      paymentAmount,
      t,
      transactionDate,
    ]
  );

  const onConfirm = useCallback(
    async ({
      selectionType,
    }: ConfirmationMethodProps): Promise<ConfirmationMethodResult> => {
      if (!userId) {
        return { shouldClose: false, success: false };
      }

      if (selectionType === SelectedPaymentType.SINGLE_VIEW) {
        const result = await handleSingleViewExecuteTransfer(String(userId));
        return result;
      }

      if (selectionType === SelectedPaymentType.BANK) {
        const result = await handleManualTransfer(String(userId));
        return result;
      }

      return { shouldClose: false, success: false };
    },
    [handleManualTransfer, handleSingleViewExecuteTransfer, userId]
  );

  return (
    <PaymentMethodModal
      open={open}
      onClose={onClose}
      amount={amount}
      setAmount={setAmount}
      gatewayPaymentId={gatewayPaymentId}
      setGatewayPaymentId={setGatewayPaymentId}
      targetContractDomainId={targetContractDomainId}
      setTargetContractDomainId={setTargetContractDomainId}
      onConfirm={onConfirm}
      paymentAmount={paymentAmount}
      userId={userId}
      transactionDate={transactionDate}
      setTransactionDate={setTransactionDate}
    />
  );
};

export default MultiCashoutModal;
